import React, { useState, forwardRef, useImperativeHandle } from "react";
import "./ContactDetails.css";
import PageIndicator from "./PageIndicator";

const ContactDetails = forwardRef(({ joinOrHire, currentMobilePage, onMobilePageChange, onSubmit, isSubmitting, storedFormData }, ref) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        files: [] // Changed from file: null to files: []
    });

    const [errors, setErrors] = useState({});

    // Add file validation constants
    const MAX_TOTAL_FILES = 3; // Maximum 3 files
    const MAX_TOTAL_SIZE = 50 * 1024 * 1024; // 50MB total

    // Restore form data when coming back from review
    React.useEffect(() => {
        if (storedFormData?.contactDetails) {
            setFormData(storedFormData.contactDetails);
        }
    }, [storedFormData]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Only validate if there's already an error for this field
        if (errors[name]) {
            const newErrors = { ...errors };

            if (name === 'name') {
                if (value.trim()) {
                    delete newErrors.name;
                }
            }

            if (name === 'email') {
                if (value.trim() && validateEmail(value)) {
                    delete newErrors.email;
                }
            }

            if (name === 'phone') {
                if (!value || validatePhone(value)) {
                    delete newErrors.phone;
                }
            }

            setErrors(newErrors);
        }
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;

        // Validate on blur
        const newErrors = { ...errors };

        if (name === 'name') {
            if (!value.trim()) {
                newErrors.name = 'Name is required';
            } else {
                delete newErrors.name;
            }
        }

        if (name === 'email') {
            if (!value.trim()) {
                newErrors.email = 'Email is required';
            } else if (!validateEmail(value)) {
                newErrors.email = 'Please enter a valid email';
            } else {
                delete newErrors.email;
            }
        }

        if (name === 'phone') {
            if (value && !validatePhone(value)) {
                newErrors.phone = 'Please enter a valid phone number';
            } else {
                delete newErrors.phone;
            }
        }

        setErrors(newErrors);
    };

    const handleFileChange = (e) => {
        const newFiles = Array.from(e.target.files);
        const currentFiles = formData.files;

        // Check number of files
        if (currentFiles.length + newFiles.length > MAX_TOTAL_FILES) {
            alert(`You can only upload a maximum of ${MAX_TOTAL_FILES} files.`);
            return;
        }

        // Check total size
        const currentTotalSize = currentFiles.reduce((sum, file) => sum + file.size, 0);
        const newTotalSize = newFiles.reduce((sum, file) => sum + file.size, 0);

        if (currentTotalSize + newTotalSize > MAX_TOTAL_SIZE) {
            alert(`Total file size would exceed ${MAX_TOTAL_SIZE / (1024 * 1024)}MB. Please remove some files or choose smaller files.`);
            return;
        }

        // All validations passed, add the files
        setFormData(prev => ({
            ...prev,
            files: [...prev.files, ...newFiles]
        }));

        // Clear any file-related errors
        if (errors.files) {
            setErrors(prev => ({
                ...prev,
                files: ''
            }));
        }
    };

    const removeFile = (indexToRemove) => {
        setFormData(prev => ({
            ...prev,
            files: prev.files.filter((_, index) => index !== indexToRemove)
        }));
    };

    const validateEmail = (email) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
    };

    const validatePhone = (phone) => {
        const re = /^[\+]?[\d\s\-\(\)]+$/;
        return re.test(phone) && phone.length >= 10;
    };

    const handleNext = () => {
        // Basic validation
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!validateEmail(formData.email)) {
            newErrors.email = 'Please enter a valid email';
        }

        if (!formData.phone.trim()) {
            newErrors.phone = 'Phone number is required';
        } else if (!validatePhone(formData.phone)) {
            newErrors.phone = 'Please enter a valid phone number';
        }

        setErrors(newErrors);

        if (Object.keys(newErrors).length === 0) {
            onMobilePageChange(1);
        }
    };

    // Pass form data to parent when needed
    React.useEffect(() => {
        if (onSubmit && typeof onSubmit === 'function') {
            // This will be called by the parent to get the current form data
            onSubmit.getContactDetails = () => formData;
        }
    }, [formData, onSubmit]);

    // Expose methods to parent component through ref
    useImperativeHandle(ref, () => ({
        getContactDetails: () => formData
    }), [formData]);

    return (
        <div className={`contact-details ${currentMobilePage === 0 ? 'mobile-active' : 'mobile-hidden'}`}>
            <div className="contact-details-content">
                <h3>
                    {joinOrHire
                        ? "Are you looking for a job opportunity?"
                        : "Tell us about your project"
                    }
                </h3>

                <div className="input-group">
                    <input
                        type="text"
                        name="name"
                        placeholder={joinOrHire ? "Your name" : "Company/Contact name"}
                        value={formData.name}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        aria-label={joinOrHire ? "Your name" : "Company or contact name"}
                        className={errors.name ? 'error' : ''}
                        required
                    />
                    {errors.name && <span className="error-text">{errors.name}</span>}
                </div>

                <div className="input-group">
                    <input
                        type="email"
                        name="email"
                        placeholder="Email address"
                        value={formData.email}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        aria-label="Email address"
                        className={errors.email ? 'error' : ''}
                        required
                    />
                    {errors.email && <span className="error-text">{errors.email}</span>}
                </div>

                <div className="input-group">
                    <input
                        type="tel"
                        name="phone"
                        placeholder="Phone number"
                        value={formData.phone}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        aria-label="Phone number"
                        className={errors.phone ? 'error' : ''}
                        required
                    />
                    {errors.phone && <span className="error-text">{errors.phone}</span>}
                </div>

                <h4>
                    {joinOrHire
                        ? "Attach documents (CV, portfolio, etc.)"
                        : "Attach project documents"
                    }
                </h4>

                <div className="file-upload-wrapper">
                    <input
                        type="file"
                        id="file-upload"
                        onChange={handleFileChange}
                        accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                        multiple
                        disabled={formData.files.length >= MAX_TOTAL_FILES}
                        aria-label={joinOrHire ? "Upload CV or portfolio" : "Upload project documents"}
                    />
                    <label
                        htmlFor="file-upload"
                        className={`file-upload-label ${formData.files.length >= MAX_TOTAL_FILES ? 'disabled' : ''}`}
                    >
                        <span className="file-placeholder">
                            {formData.files.length >= MAX_TOTAL_FILES
                                ? "Maximum files reached"
                                : "📎 Choose files or drag and drop"
                            }
                        </span>
                        <span className="file-limits">
                            (Max {MAX_TOTAL_FILES} files, {MAX_TOTAL_SIZE / (1024 * 1024)}MB total)
                        </span>
                    </label>
                </div>

                {formData.files.length > 0 && (
                    <div className="files-info">
                        <h5>Selected Files ({formData.files.length}):</h5>
                        {formData.files.map((file, index) => (
                            <div key={index} className="file-item">
                                <span className="file-name">📄 {file.name}</span>
                                <span className="file-size">
                                    ({(file.size / 1024 / 1024).toFixed(2)} MB)
                                </span>
                                <button
                                    type="button"
                                    className="remove-file-btn"
                                    onClick={() => removeFile(index)}
                                    aria-label={`Remove ${file.name}`}
                                >
                                    ✕
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Mobile navigation - only visible on mobile */}
            <div className="mobile-navigation">
                <PageIndicator
                    currentPage={currentMobilePage}
                    totalPages={4}
                />
                <button
                    type="button"
                    onClick={handleNext}
                    className="mobile-next-btn"
                >
                    Next
                </button>
            </div>
        </div>
    );
});

export default ContactDetails;
